// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import {
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProgressIndicator, { ProgressStep } from "@mcmc/App/Components/ProgressIndicator";
import { ProgressStepStyles } from "@mcmc/App/Components/ProgressIndicator/ProgressStep";
import setCurrentStep from "@mcmc/App/Handlers/Pages/Checkout/SetCurrentStep";
import setNextPreviousHandlers from "@mcmc/App/Handlers/Pages/Checkout/SetNextPreviousHandlers";
import { initializeScrollTo } from "@mcmc/App/Libraries";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getCurrentStep } from "@mcmc/App/Store/Selectors/CheckoutReviewAndSubmitSelectors";
import {
    CheckoutStep,
    CheckoutStepKey,
    getCheckoutSteps,
} from "@mcmc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmit.helper";
import Edit from "@mcmc/XDL/Icons/Edit";
import { resetRetainScrollPosition } from "@mcmc/XDL/Utilities";
import keyBy from "lodash/keyBy";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ step: string; express?: boolean }>(location.search);
    const isExpressCheckout = parsedQuery.express;

    return {
        fulfillmentMethod: state.context.session.fulfillmentMethod,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        currentStep: getCurrentStep(state),
        validatePayment: state.xCheckout.validatePayment,
        cartId: state.pages.checkoutReviewAndSubmit.cartId,
        isSubmitForApprovalOrder: canSubmitForApprovalOrder(
            state.pages.checkoutReviewAndSubmit.cartId
                ? getCartState(state, state.pages.checkoutReviewAndSubmit.cartId).value
                : getCurrentCartState(state).value,
        ),
        hash: parsedQuery.step,
        isExpressCheckout,
    };
};

const mapDispatchToProps = {
    setCurrentStep,
    setNextPreviousHandlers,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    HasHistory;

interface ProgressBarStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    hideWrapper?: InjectableCss;
    progressStep?: ProgressStepStyles;
    editStepLink?: LinkPresentationProps;
}

const progressStepStyle: InjectableCss = {
    css: css`
        flex: 1 1 222px;
        max-width 222px;
    `,
};

export const progressBarStyles: ProgressBarStyles = {
    wrapper: {
        css: css`
            margin-bottom: 40px;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
        `,
    },
    hideWrapper: {
        css: css`
            display: none;
        `,
    },
    progressStep: {
        step: {
            next: progressStepStyle,
            current: progressStepStyle,
            passed: progressStepStyle,
        },
    },
    editStepLink: {
        color: "secondary",
        icon: {
            iconProps: {
                src: Edit,
                size: 16,
                css: css`
                    margin: 5px;
                `,
            },
        },
    },
};

const styles = progressBarStyles;

function goToStep(currentStep: CheckoutStep, nextStep: CheckoutStep, allSteps: CheckoutStep[], setCurrentStep: any) {
    if (currentStep.index <= nextStep.index) {
        checkIfAllPreviousStepsIsValid(currentStep.index, nextStep, allSteps, setCurrentStep).then(
            () => {
                if (nextStep.isDisabled) {
                    nextStep = allSteps.find((step, i) => !step.isDisabled && i > nextStep.index) || nextStep;
                }
                setCurrentStep({ currentStep: { ...nextStep } });
            },
            () => {
                initializeScrollTo("[aria-invalid='true']", -50);
            },
        );
    } else {
        setCurrentStep({ currentStep: { ...nextStep } });
    }
}

function checkIfAllPreviousStepsIsValid(
    currentStepIndex: number,
    nextStep: CheckoutStep,
    allSteps: CheckoutStep[],
    setCurrentStep: any,
): Promise<boolean> {
    if (currentStepIndex >= nextStep.index) {
        const validationPromise = new Promise<boolean>(resolve => {
            resolve(true);
        });
        return validationPromise;
    }

    return allSteps[currentStepIndex].validate().then(
        () => {
            return checkIfAllPreviousStepsIsValid(currentStepIndex + 1, nextStep, allSteps, setCurrentStep);
        },
        error => {
            setCurrentStep({ currentStep: { ...allSteps[currentStepIndex] } });
            throw error;
        },
    );
}

function goToNextStep(currentStep: CheckoutStep, allSteps: CheckoutStep[], setCurrentStep: any) {
    allSteps[currentStep.index].validate().then(
        () => {
            const nextStep = allSteps.find((step, i) => !step.isDisabled && i > currentStep.index);
            if (nextStep) {
                setCurrentStep({ currentStep: { ...nextStep } });
                initializeScrollTo("#checkout-progress-bar");
            }
        },
        () => {
            initializeScrollTo("[aria-invalid='true']", -50);
        },
    );
}

function goToPreviousStep(
    currentStep: CheckoutStep,
    allSteps: CheckoutStep[],
    setCurrentStep: any,
    moveToAddressesPage: () => void,
) {
    const previousStep = allSteps.filter((step, i) => !step.isDisabled && i < currentStep.index).pop();
    if (previousStep) {
        if (previousStep.key === CheckoutStepKey.addresses) {
            moveToAddressesPage();
        } else {
            setCurrentStep({ currentStep: { ...previousStep } });
            initializeScrollTo("#checkout-progress-bar");
        }
    }
}

const CheckoutReviewAndPayProgressBar: FC<Props> = ({
    id,
    currentStep,
    shellContext,
    history,
    checkoutShippingPageUrl,
    cartPageUrl,
    cartId,
    isSubmitForApprovalOrder,
    isExpressCheckout,
    validatePayment,
    fulfillmentMethod,
    hash,
    setCurrentStep,
    setNextPreviousHandlers,
}) => {
    const [steps, setSteps] = useState<CheckoutStep[]>(getCheckoutSteps(fulfillmentMethod));
    const stepsMap = keyBy(steps, "key");
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    useEffect(() => {
        if (validatePayment || isSubmitForApprovalOrder) {
            const paymentStep = stepsMap[CheckoutStepKey.payment];
            if (paymentStep) {
                paymentStep.validate = validatePayment || paymentStep.validate;
                paymentStep.isDisabled = isSubmitForApprovalOrder;
                setSteps([...steps]);
                if (isFirstLoad) {
                    setIsFirstLoad(false);
                    const initialStep = stepsMap[hash] || steps[2];
                    goToStep(steps[2], initialStep, steps, setCurrentStep);
                }
            }
        }
    }, [validatePayment, isSubmitForApprovalOrder]);

    const scrollToCurrentStep = () => {
        const progressContainer = document.getElementById("checkout-progress-bar");

        if (!progressContainer) {
            return;
        }

        const containerScrollWidth = progressContainer.scrollWidth;
        const containerClientWidth = progressContainer.clientWidth;
        const isContainerScrollable = containerScrollWidth > containerClientWidth;
        const childSteps = progressContainer.children;
        const dontScroll = !isContainerScrollable || currentStep!.isDisabled || !childSteps.length;
        const activeChildStep = childSteps.item(currentStep!.index) as HTMLElement;

        if (dontScroll || !activeChildStep) {
            return;
        }

        progressContainer.scrollTo(activeChildStep.offsetLeft, 0);
    };

    useEffect(() => {
        if (currentStep) {
            setNextPreviousHandlers({
                goToPreviousStep: () => {
                    currentStep && goToPreviousStep(currentStep, steps, setCurrentStep, moveToAddressesPage);
                },
                goToNextStep: () => {
                    currentStep && goToNextStep(currentStep, steps, setCurrentStep);
                },
            });
            scrollToCurrentStep();
        }
    }, [currentStep, steps]);

    if (!currentStep) {
        currentStep = steps[2];
    }

    const displayAllSteps = shellContext.isEditing || currentStep!.key === CheckoutStepKey.review;

    const moveToAddressesPage = () => {
        let postFix = "";
        if (cartId) {
            postFix = `?cartId=${cartId}`;
        }
        if (isExpressCheckout) {
            postFix += `${postFix ? "&" : "?"}express=true`;
        }

        resetRetainScrollPosition(checkoutShippingPageUrl + postFix);
        history.push(checkoutShippingPageUrl + postFix);
    };

    const moveToCartPage = () => {
        history.push(cartPageUrl || "");
    };

    const onClickHandler = (destStep: CheckoutStep) => {
        switch (destStep.key) {
            case CheckoutStepKey.cart:
                moveToCartPage();
                break;
            case CheckoutStepKey.addresses:
                moveToAddressesPage();
                break;
            default:
                goToStep(currentStep!, destStep, steps, setCurrentStep);
        }
    };

    return (
        <>
            <ProgressIndicator id="checkout-progress-bar">
                {steps.map(step => (
                    <ProgressStep
                        key={step.key}
                        index={step.index}
                        label={step.title}
                        current={currentStep!.index}
                        icon={step.icon}
                        onClick={step.isDisabled ? undefined : () => onClickHandler(step)}
                        disabled={step.isDisabled}
                        extendedStyles={styles.progressStep}
                    />
                ))}
            </ProgressIndicator>
            <StyledWrapper {...(displayAllSteps ? styles.wrapper : styles.hideWrapper)}>
                <StyledWrapper {...styles.titleWrapper}>
                    {stepsMap[CheckoutStepKey.addresses].titleDisplay}
                    {displayAllSteps && (
                        <Link
                            {...styles.editStepLink}
                            onClick={() => onClickHandler(stepsMap[CheckoutStepKey.addresses])}
                            aria-label={translate("Edit {0}", stepsMap[CheckoutStepKey.addresses].title)}
                        ></Link>
                    )}
                </StyledWrapper>
                <Zone zoneName="Content2" contentId={id} />
            </StyledWrapper>
            {stepsMap[CheckoutStepKey.shipping] && (
                <StyledWrapper
                    {...(displayAllSteps || currentStep!.key === CheckoutStepKey.shipping
                        ? styles.wrapper
                        : styles.hideWrapper)}
                >
                    <StyledWrapper {...styles.titleWrapper}>
                        {stepsMap[CheckoutStepKey.shipping] && stepsMap[CheckoutStepKey.shipping].titleDisplay}
                        {displayAllSteps && (
                            <Link
                                {...styles.editStepLink}
                                onClick={() => onClickHandler(stepsMap[CheckoutStepKey.shipping])}
                                aria-label={translate("Edit {0}", stepsMap[CheckoutStepKey.shipping].title)}
                            ></Link>
                        )}
                    </StyledWrapper>
                    <Zone zoneName="Content0" contentId={id} />
                </StyledWrapper>
            )}
            {stepsMap[CheckoutStepKey.payment] && !stepsMap[CheckoutStepKey.payment].isDisabled && (
                <StyledWrapper
                    {...(displayAllSteps || currentStep!.key === CheckoutStepKey.payment
                        ? styles.wrapper
                        : styles.hideWrapper)}
                >
                    <StyledWrapper id="payment-section" {...styles.titleWrapper}>
                        {stepsMap[CheckoutStepKey.payment].titleDisplay}
                        {displayAllSteps && (
                            <Link
                                {...styles.editStepLink}
                                onClick={() => onClickHandler(stepsMap[CheckoutStepKey.payment])}
                                aria-label={translate("Edit {0}", stepsMap[CheckoutStepKey.payment].title)}
                            ></Link>
                        )}
                    </StyledWrapper>
                    <Zone zoneName="Content1" contentId={id} />
                </StyledWrapper>
            )}
            <StyledWrapper {...(displayAllSteps ? styles.wrapper : styles.hideWrapper)}>
                <Zone zoneName="Content3" contentId={id} />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(
        withHistory(connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndPayProgressBar)),
    ),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: [CheckoutReviewAndSubmitPageContext],
        displayName: "Checkout Progress Bar",
    },
};

export default widgetModule;
